/**
 * Projects collection — typed CRUD on top of the generic store.
 *
 * A Project is the delivery workspace between a Client (Account) and its
 * Tasks: Clients → Projects → Tasks. This adapter mirrors `tasks.ts` —
 * it centralises id/timestamp minting, timeline appending, and the inline
 * collaboration mutations (comments, attachments, links). Tasks link back
 * to a project via `ProjectTask.projectId`; completion % is derived from
 * those tasks in the view, never stored here.
 */

import { db, nowIso, uuid } from './store';
import type {
  Project,
  ProjectLink,
  ProjectStatus,
  TaskEvent,
  TaskPriority,
  WorkAttachment,
  WorkComment,
} from './types';
import { seedProjects } from './seed';

/** Valid project statuses, ordered for pickers. */
export const PROJECT_STATUSES: readonly ProjectStatus[] = [
  'active',
  'on-hold',
  'completed',
  'archived',
] as const;

const event = (
  kind: TaskEvent['kind'],
  actor: string,
  text: string,
  at = nowIso(),
): TaskEvent => ({ id: uuid(), at, actor, kind, text });

export async function listProjects(): Promise<Project[]> {
  return db.list('projects');
}

export async function getProject(id: string): Promise<Project | null> {
  return db.get('projects', id);
}

export function subscribeProjects(fn: (records: Project[]) => void): () => void {
  return db.subscribe('projects', fn);
}

/** Projects belonging to one client (Account). */
export async function listProjectsForClient(clientId: string): Promise<Project[]> {
  const all = await db.list('projects');
  return all.filter((p) => p.clientId === clientId);
}

export interface NewProjectInput {
  clientId: string;
  clientName: string;
  title: string;
  description?: string;
  status?: ProjectStatus;
  priority?: TaskPriority;
  dueDate?: string | null;
  tags?: string[];
  ownerName?: string;
  memberNames?: string[];
  links?: { label: string; url: string }[];
  createdBy?: string;
}

export async function createProject(input: NewProjectInput): Promise<Project> {
  const ts = nowIso();
  const createdBy = input.createdBy ?? 'You';
  const record: Project = {
    id: uuid(),
    clientId: input.clientId,
    clientName: input.clientName,
    title: input.title,
    description: input.description,
    status: input.status ?? 'active',
    priority: input.priority ?? 'medium',
    dueDate: input.dueDate ?? null,
    tags: input.tags ?? [],
    ownerName: input.ownerName,
    memberNames: input.memberNames ?? [],
    links: (input.links ?? []).map((l) => ({ id: uuid(), label: l.label, url: l.url })),
    comments: [],
    attachments: [],
    createdBy,
    timeline: [event('created', createdBy, 'Created project', ts)],
    createdAt: ts,
    updatedAt: ts,
  };
  await db.add('projects', record);
  return record;
}

/**
 * Patch a project. Re-stamps `updatedAt` and appends timeline events for
 * status changes plus a catch-all note for other field edits.
 */
export async function updateProject(
  id: string,
  patch: Partial<
    Omit<Project, 'id' | 'createdAt' | 'timeline' | 'comments' | 'attachments' | 'links'>
  >,
  actor = 'You',
): Promise<Project> {
  const existing = await db.get('projects', id);
  if (!existing) throw new Error(`No project ${id}`);
  const ts = nowIso();

  const events: TaskEvent[] = [];
  if (patch.status && patch.status !== existing.status) {
    events.push(event('status', actor, `Status: ${existing.status} → ${patch.status}`, ts));
  }
  const otherKeys = Object.keys(patch).filter(
    (k) => k !== 'status' && k !== 'updatedAt',
  );
  if (otherKeys.length > 0) {
    events.push(event('note', actor, `Updated ${otherKeys.join(', ')}`, ts));
  }

  return db.update('projects', id, {
    ...patch,
    timeline: [...existing.timeline, ...events],
    updatedAt: ts,
  });
}

/** Add a threaded comment. `mentions` are the roster names @tagged in the body. */
export async function addProjectComment(
  id: string,
  authorName: string,
  body: string,
  mentions: string[] = [],
): Promise<Project> {
  const existing = await db.get('projects', id);
  if (!existing) throw new Error(`No project ${id}`);
  const ts = nowIso();
  const comment: WorkComment = { id: uuid(), authorName, body, mentions, createdAt: ts };
  return db.update('projects', id, {
    comments: [...existing.comments, comment],
    timeline: [...existing.timeline, event('note', authorName, 'Commented', ts)],
    updatedAt: ts,
  });
}

/** Pin a file or link attachment. */
export async function addProjectAttachment(
  id: string,
  attachment: Omit<WorkAttachment, 'id' | 'addedAt'>,
): Promise<Project> {
  const existing = await db.get('projects', id);
  if (!existing) throw new Error(`No project ${id}`);
  const ts = nowIso();
  const record: WorkAttachment = { ...attachment, id: uuid(), addedAt: ts };
  return db.update('projects', id, {
    attachments: [...existing.attachments, record],
    timeline: [...existing.timeline, event('note', attachment.addedBy, `Attached ${attachment.label}`, ts)],
    updatedAt: ts,
  });
}

export async function removeProjectAttachment(id: string, attachmentId: string): Promise<Project> {
  const existing = await db.get('projects', id);
  if (!existing) throw new Error(`No project ${id}`);
  return db.update('projects', id, {
    attachments: existing.attachments.filter((a) => a.id !== attachmentId),
    updatedAt: nowIso(),
  });
}

/** Add an external link. */
export async function addProjectLink(
  id: string,
  label: string,
  url: string,
): Promise<Project> {
  const existing = await db.get('projects', id);
  if (!existing) throw new Error(`No project ${id}`);
  const link: ProjectLink = { id: uuid(), label, url };
  return db.update('projects', id, {
    links: [...existing.links, link],
    updatedAt: nowIso(),
  });
}

export async function removeProjectLink(id: string, linkId: string): Promise<Project> {
  const existing = await db.get('projects', id);
  if (!existing) throw new Error(`No project ${id}`);
  return db.update('projects', id, {
    links: existing.links.filter((l) => l.id !== linkId),
    updatedAt: nowIso(),
  });
}

export async function deleteProject(id: string): Promise<void> {
  await db.remove('projects', id);
}

/** Seed the projects collection on first run. Idempotent. */
export async function bootstrapProjectsIfEmpty(): Promise<void> {
  const existing = await db.list('projects');
  if (existing.length > 0) return;
  await db.replaceAll('projects', seedProjects);
}

/** Reset the projects collection back to seed data. Useful for dev. */
export async function resetProjectsToSeed(): Promise<void> {
  await db.replaceAll('projects', seedProjects);
}
